// The leaves every feedback section is built from.
//
// Plain React text nodes throughout. No markdown, no dangerouslySetInnerHTML,
// no sanitiser and no new dependency: every value in all three output
// contracts is a string, number, null or array of those, and the prompts
// forbid fences. Model output must not reach the DOM as markup.
//
// No mathjax-process on this subtree either. TARA writing is prose, and a
// dollar sign in a student's quoted sentence would swallow the rest of the
// block.

import React from 'react';
import { taraEvidenceBasisLabel, taraTaskStatusChip, taraTraitLabel } from '../utils/taraFeedbackStatus';
import { taraQuotedSpans } from '../utils/taraEssaySpans';

// A nullable object renders nothing at all. The contracts forbid emitting an
// object of nulls in place of null, but an empty card still reads to a student
// as a finding that failed to load, so guard on content rather than presence.
export function NullableBlock({ value, children }) {
  if (!value || typeof value !== 'object') return null;
  const hasContent = Object.values(value).some(
    (v) => (typeof v === 'string' && v.trim())
      || typeof v === 'number'
      || (Array.isArray(v) && v.length),
  );
  if (!hasContent) return null;
  return children;
}

export function Prose({ children, className = '' }) {
  if (!children || (typeof children === 'string' && !children.trim())) return null;
  // Never hand React an object. Several fields in the stage 3 contract are
  // objects and were being passed here as though they were strings, which
  // throws and takes the whole section down. DetailBlock renders those; this
  // refuses them so a future one degrades to nothing rather than a crash.
  if (typeof children === 'object' && !Array.isArray(children)
      && !React.isValidElement(children)) return null;
  return <p className={`tara-fb-prose ${className}`.trim()}>{children}</p>;
}

// The order these read in. Prompt 3 returns several objects of prose fields,
// and the student wants the headline first and the caveats last, not the
// alphabetical order the JSON happens to carry.
const DETAIL_ORDER = [
  'headline', 'focus', 'reasoning_move', 'current_move', 'next_move',
  'instruction', 'proposition_or_scenario', 'explanation', 'bridging_logic',
  'why_it_matters', 'why_it_matters_here', 'reusable_rule', 'expected_time',
];

// One object of prose fields. Strings render as paragraphs, arrays of strings
// as a list, and anything else is skipped.
export function DetailBlock({ value, className = '' }) {
  if (!value) return null;
  if (typeof value === 'string') return <Prose className={className}>{value}</Prose>;
  if (typeof value !== 'object' || Array.isArray(value)) return null;

  const ordered = DETAIL_ORDER.filter((k) => value[k]);
  const rest = Object.keys(value).filter(
    (k) => !DETAIL_ORDER.includes(k) && !k.endsWith('_span_ids') && !k.endsWith('_label')
      && value[k],
  );
  const parts = [...ordered, ...rest];
  if (!parts.length) return null;

  return (
    <>
      {parts.map((key) => {
        const v = value[key];
        if (typeof v === 'string' && v.trim()) {
          return <Prose key={key} className={className}>{v}</Prose>;
        }
        if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
          return (
            <ul key={key} className="tara-fb-list">
              {v.filter((x) => x && x.trim()).map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          );
        }
        return null;
      })}
    </>
  );
}

export function Heading({ children }) {
  if (!children) return null;
  return <h4 className="tara-fb-heading">{children}</h4>;
}

// A heading the model wrote, not one of ours.
//
// Heading small-caps our own fixed labels, which are two or three words. The
// model's labels are sentence length, and setting a sentence in capitals makes
// it hard to read: "AWARENESS THAT A CONCLUSION NEEDS AN ARGUED LINK TO ITS
// PREMISE" appeared on the live site.
export function FindingHeading({ children }) {
  if (!children || typeof children !== 'string' || !children.trim()) return null;
  return <h4 className="tara-fb-finding-heading">{children}</h4>;
}

// Null renders an em dash, never a zero. A missing mark and a mark of nothing
// are different things.
export function ScoreResult({ result }) {
  if (!result || typeof result !== 'object') return null;
  const mark = typeof result.mark_out_of_100 === 'number'
    ? result.mark_out_of_100
    : (typeof result.score_out_of_100 === 'number' ? result.score_out_of_100 : null);
  const band = result.practice_band;
  const showBand = band && String(band).trim().toUpperCase() !== 'UNMAPPED';

  return (
    <div className="tara-fb-result">
      <div className="tara-fb-result-head">
        {result.label ? <span className="tara-fb-result-label">{result.label}</span> : null}
        <span className="tara-fb-score">
          {mark === null ? '—' : mark}
          <span className="tara-fb-score-of">/100</span>
        </span>
        {showBand ? <span className="tara-fb-band">{band}</span> : null}
      </div>
      <Prose>{result.result_line || result.explanation}</Prose>
    </div>
  );
}

export function EvidenceQuotes({ spans, spanIds }) {
  const { shown, hidden } = taraQuotedSpans(spans, spanIds, 2);
  if (!shown.length) return null;
  return (
    <div className="tara-fb-quotes">
      {shown.map((text, i) => (
        <blockquote key={i} className="tara-fb-quote">{`“${text}”`}</blockquote>
      ))}
      {hidden ? (
        <p className="tara-fb-quote-more">{`and ${hidden} more from your essay`}</p>
      ) : null}
    </div>
  );
}

export function EvidenceBasis({ children }) {
  const label = taraEvidenceBasisLabel(children);
  if (!label) return null;
  return <p className="tara-fb-basis">{`Based on ${label}`}</p>;
}

export function TaskStatusRow({ task, spans }) {
  if (!task || typeof task !== 'object') return null;
  const chip = taraTaskStatusChip(task.status);
  return (
    <div className="tara-fb-task">
      <div className="tara-fb-task-head">
        <span className="tara-fb-task-name">{task.task || 'This task'}</span>
        <span className={`tara-fb-chip ${chip.tone}`}>{chip.label}</span>
      </div>
      <Prose>{task.student_explanation}</Prose>
      <EvidenceQuotes spans={spans} spanIds={task.evidence_span_ids} />
      <EvidenceBasis>{task.evidence_basis}</EvidenceBasis>
    </div>
  );
}

// A five segment meter rather than stars or a bare number. Stars read as a
// rating of the person; a meter reads as a position on a described scale,
// which is what the rubric levels are.
//
// A null level shows "Not assessed" and never a zero-filled meter. Level 0
// means absent or materially distorted, which is not the same as unknown.
export function TraitLevelRow({ trait }) {
  if (!trait || typeof trait !== 'object') return null;
  const label = taraTraitLabel(trait.trait, trait.label);
  if (!label) return null;
  const level = Number.isInteger(trait.level_0_to_5) ? trait.level_0_to_5 : null;

  return (
    <div className="tara-fb-trait">
      <span className="tara-fb-trait-name">{label}</span>
      {level === null ? (
        <span className="tara-fb-trait-none">Not assessed</span>
      ) : (
        <span
          className="tara-fb-meter"
          role="img"
          aria-label={`${label}: level ${level} of 5`}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={`tara-fb-meter-seg ${i < level ? 'on' : ''}`} />
          ))}
          <span className="tara-fb-trait-num">{level}</span>
        </span>
      )}
      <Prose className="tara-fb-trait-note">{trait.student_explanation}</Prose>
    </div>
  );
}

// An empty list renders nothing. Not an empty frame, and not "no strengths
// found", which reads as a verdict rather than an absence.
export function StrengthList({ items, spans, whyKey }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <div className="tara-fb-strengths">
      {items.slice(0, 4).map((item, i) => (
        <div key={i} className="tara-fb-strength">
          <FindingHeading>{item.label}</FindingHeading>
          <Prose>{item.explanation}</Prose>
          <Prose className="tara-fb-soft">{item[whyKey]}</Prose>
          <EvidenceQuotes spans={spans} spanIds={item.evidence_span_ids} />
        </div>
      ))}
    </div>
  );
}

// The contracts' designated place for an honest caveat, already written in
// language a student can read. Quiet inset strip, no icon, no colour alarm.
export function Caveat({ children }) {
  if (!children) return null;
  return <p className="tara-fb-caveat">{children}</p>;
}
